/**
 * use-finance — finance API hooks（ADM-FIN-S001~S006 / S009 / S010）
 */

import type {
  AcknowledgeAdaptyEventReviewRequest,
  AcknowledgeAdaptyEventReviewResponse,
  AdaptyEventReviewAdmissionKind,
  AdaptyEventReviewErrorCode,
  AdaptyEventReviewQueueRow,
  AdaptyEventReviewResolutionStatus,
  BonusStatsResponse,
  FinanceIncomeStatsResponse,
  FinanceRefundStatsResponse,
  FinanceRefundTrendResponse,
  GrantBonusRequest,
  GrantBonusResponse,
  RefundNoteResponse,
  RefundRecordResponse,
  TransactionImportAttributionRequest,
  TransactionImportAttributionResponse,
  TransactionImportAttributionStatus,
  TransactionImportQueueRow,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';

/** 时间窗查询参数（ADM-FIN 页头时间范围控件）：period 快捷档（24h/7d/30d/90d/ytd）或自定义 from/to(ISO) */
export type FinanceTimeQuery = { period?: string; from?: string; to?: string };
function timeQ(time?: FinanceTimeQuery): Record<string, string> {
  if (!time) return {};
  return {
    ...(time.period ? { period: time.period } : {}),
    ...(time.from ? { from: time.from } : {}),
    ...(time.to ? { to: time.to } : {}),
  };
}

export const financeApi = {
  incomeStats: (appId?: string, time?: FinanceTimeQuery) =>
    apiRequest<FinanceIncomeStatsResponse>({
      method: 'GET',
      path: '/api/v1/finance/stats/income',
      query: { ...(appId ? { app_id: appId } : {}), ...timeQ(time) },
    }),
  refundStats: (appId?: string, time?: FinanceTimeQuery) =>
    apiRequest<FinanceRefundStatsResponse>({
      method: 'GET',
      path: '/api/v1/finance/stats/refund',
      query: { ...(appId ? { app_id: appId } : {}), ...timeQ(time) },
    }),
  refundTrend: (appId?: string, time?: FinanceTimeQuery) =>
    apiRequest<FinanceRefundTrendResponse>({
      method: 'GET',
      path: '/api/v1/finance/stats/refund-trend',
      query: { ...(appId ? { app_id: appId } : {}), ...timeQ(time) },
    }),
  // ADM-FIN-S003 退款记录 offset 分页（admin 列表默认 offset，dev-docs 40-分页与排序）：envelope 顶层
  // total/page/limit/hasMore（apiRequestOffsetPaged）。app_id 随 App scope 注入（'all' → 不传 = 全量汇总）。
  refunds: (
    appId?: string,
    page?: number,
    limit = 200,
  ): Promise<OffsetPagedResult<RefundRecordResponse>> =>
    apiRequestOffsetPaged<RefundRecordResponse>({
      method: 'GET',
      path: '/api/v1/finance/refunds',
      query: {
        ...(appId ? { app_id: appId } : {}),
        ...(page ? { page } : {}),
        limit,
      },
    }),
  // ADM-FIN-S004 配套：BONUS 发放月度统计（平台级，无 app_id；current/previous 供前端算环比）
  bonusStats: () =>
    apiRequest<BonusStatsResponse>({ method: 'GET', path: '/api/v1/finance/stats/bonus' }),
  grantBonus: (body: GrantBonusRequest) =>
    apiRequest<GrantBonusResponse>({
      method: 'POST',
      path: '/api/v1/finance/grant-bonus',
      body,
      // 该端点没有请求级幂等键；401 自动 refresh 后重放可能造成重复到账。
      replayOnUnauthorized: false,
    }),
  // ADM-FIN-S003 退款单运营备注（admin 独有表）：GET 读 / PATCH 写。:id = 退款行 UUID。
  getRefundNote: (
    refundId: string,
    environment: RefundRecordResponse['environment'] = 'Production',
  ) =>
    apiRequest<RefundNoteResponse>({
      method: 'GET',
      path: `/api/v1/finance/refunds/${refundId}/note`,
      query: { environment },
    }),
  updateRefundNote: (
    refundId: string,
    note: string,
    environment: RefundRecordResponse['environment'] = 'Production',
  ) =>
    apiRequest<RefundNoteResponse>({
      method: 'PATCH',
      path: `/api/v1/finance/refunds/${refundId}/note`,
      query: { environment },
      body: { note },
    }),
  // ADM-FIN-S009 交易导入待归属队列（viewer+，offset 分页）。attribution_status 默认后端取
  // PERMANENT_UNATTRIBUTED（DLQ 交接口径）；app_id 随 App scope 注入（'all' → 不传 = 全量）。
  transactionImports: (query: {
    attribution_status?: TransactionImportAttributionStatus;
    app_id?: string;
    /** 按 lineage 服务端等值过滤（A1 §10.0）——permanent_ambiguous_lineage 台账行的交接入口 */
    original_transaction_id?: string;
    page?: number;
    limit?: number;
  }): Promise<OffsetPagedResult<TransactionImportQueueRow>> =>
    apiRequestOffsetPaged<TransactionImportQueueRow>({
      method: 'GET',
      path: '/api/v1/finance/transaction-imports',
      query: {
        ...(query.attribution_status ? { attribution_status: query.attribution_status } : {}),
        ...(query.app_id ? { app_id: query.app_id } : {}),
        ...(query.original_transaction_id
          ? { original_transaction_id: query.original_transaction_id }
          : {}),
        ...(query.page ? { page: query.page } : {}),
        limit: query.limit ?? 50,
      },
    }),
  // ADM-FIN-S009 人工归属（operator+）：写 owner_user_id + 审计。无请求级幂等键 → 401 续期后禁自动
  // 重放（避免重复改绑；同 grantBonus 口径）。
  attributeTransactionImport: (body: TransactionImportAttributionRequest) =>
    apiRequest<TransactionImportAttributionResponse>({
      method: 'POST',
      path: '/api/v1/finance/transaction-imports/attribution',
      body,
      replayOnUnauthorized: false,
    }),
  // ADM-FIN-S010 webhook 事件人工待办（viewer+，offset）。resolution_status 默认 OPEN；app_id 走读侧作用域。
  adaptyEventReviews: (query: {
    resolution_status?: AdaptyEventReviewResolutionStatus;
    admission_kind?: AdaptyEventReviewAdmissionKind;
    error_code?: AdaptyEventReviewErrorCode;
    app_id?: string;
    page?: number;
    limit?: number;
  }): Promise<OffsetPagedResult<AdaptyEventReviewQueueRow>> =>
    apiRequestOffsetPaged<AdaptyEventReviewQueueRow>({
      method: 'GET',
      path: '/api/v1/finance/adapty-event-reviews',
      query: {
        ...(query.resolution_status ? { resolution_status: query.resolution_status } : {}),
        ...(query.admission_kind ? { admission_kind: query.admission_kind } : {}),
        ...(query.error_code ? { error_code: query.error_code } : {}),
        ...(query.app_id ? { app_id: query.app_id } : {}),
        ...(query.page ? { page: query.page } : {}),
        limit: query.limit ?? 50,
      },
    }),
  // ADM-FIN-S010 人工确认闭环（operator+ MFA）：只写台账状态，不触发重放。无请求级幂等键 → 禁 401 续期重放。
  acknowledgeAdaptyEventReview: (body: AcknowledgeAdaptyEventReviewRequest) =>
    apiRequest<AcknowledgeAdaptyEventReviewResponse>({
      method: 'POST',
      path: '/api/v1/finance/adapty-event-reviews/acknowledge',
      body,
      replayOnUnauthorized: false,
    }),
};
